import type { MultiSelectOption } from '../../../components/MultiSelect';

export interface MetricFilterRow {
  region: string;
  lc: string;
  lc_name: string;
  key_status: string;
  customer: string;
  customer_name: string;
  location: string;
  location_name: string;
}
export interface MetricFilterSelection {
  regions: string[];
  lcs: string[];
  keyStatuses: string[];
  customers: string[];
  locations: string[];
}
export const expandLcs = (values: string[]) => Array.from(new Set(values.flatMap(v => v.split('~')).filter(Boolean)));

/** A facet respects every OTHER selection, allowing multi-select within itself.
 * Keep selected options visible even when a date/region change yields no matches,
 * so the user can see and remove the constraint rather than silently losing it.
 */
export function metricFilterOptions(rows: MetricFilterRow[], selected: MetricFilterSelection, lcRoster: MultiSelectOption[]) {
  const lcs = new Set(expandLcs(selected.lcs));
  const matches = (row: MetricFilterRow, facet: 'lc' | 'customer' | 'location') =>
    (!selected.regions.length || selected.regions.includes(row.region)) &&
    (!selected.keyStatuses.length || selected.keyStatuses.includes(row.key_status)) &&
    (facet === 'lc' || !lcs.size || lcs.has(row.lc)) &&
    (facet === 'customer' || !selected.customers.length || selected.customers.includes(row.customer)) &&
    (facet === 'location' || !selected.locations.length || selected.locations.includes(row.location));
  const options = (facet: 'lc' | 'customer' | 'location', values: string[]) => {
    const names = new Map(rows.map(r => [r[facet], r[`${facet}_name`]]));
    const allowed = new Set(rows.filter(r => matches(r, facet)).map(r => r[facet]));
    let choices: MultiSelectOption[];
    if (facet === 'lc') {
      const roster = new Map(lcRoster.map(o => [o.value, o]));
      for (const [value, label] of names) if (value && !roster.has(value)) roster.set(value, { value, label: label || value });
      choices = [...roster.values()].filter(o => o.value.split('~').some(u => allowed.has(u)) || values.includes(o.value));
    } else {
      choices = [...new Set([...allowed, ...values])].filter(v => v && v !== '0')
        .map(value => ({ value, label: names.get(value) || value }));
    }
    for (const value of values) if (!choices.some(o => o.value === value)) choices.push({ value, label: names.get(value) || value });
    return choices.sort((a, b) => a.label.localeCompare(b.label));
  };
  return { lcs: options('lc', selected.lcs), customers: options('customer', selected.customers), locations: options('location', selected.locations) };
}
